import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { AlertCircle, CalendarCheck, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { exchangeMagicLink, getMagicLinkPreview, MagicLinkPreview } from '../services/auth.service';
import { capitalize } from '../lib/utils';

/** The email carries ?token=; #token= is accepted too, for links that must never reach a server log. */
export const readMagicToken = (search: string, hash: string): string => {
  const fromQuery = new URLSearchParams(search).get('token');
  if (fromQuery) return fromQuery;
  return new URLSearchParams(hash.replace(/^#/, '')).get('token') || '';
};

const pageClass =
  'min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4';

/**
 * Landing page of the monthly reminder email. Shows who is about to log in and enters the app only
 * on an explicit click (a POST), so mail scanners that prefetch links never spend the link.
 */
const MagicLogin: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { applyAuthPayload } = useAuth();
  const dateLocale = i18n.language?.startsWith('it') ? it : enUS;

  // Read the token once, then take it out of the address bar (history, screenshots, browser sync).
  const [token] = useState(() => readMagicToken(location.search, location.hash));
  const [preview, setPreview] = useState<MagicLinkPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [entering, setEntering] = useState(false);
  const [error, setError] = useState<'' | 'missingToken' | 'invalid' | 'exchange'>('');

  useEffect(() => {
    if (window.location.search || window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token) {
        setError('missingToken');
        setLoading(false);
        return;
      }
      try {
        const data = await getMagicLinkPreview(token);
        if (cancelled) return;
        // Nobody is logged in yet, so LanguageProvider cannot know the tenant: speak its language anyway.
        if (data.language && i18n.language !== data.language) {
          await i18n.changeLanguage(data.language);
        }
        setPreview(data);
      } catch {
        if (!cancelled) setError('invalid');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n is a stable singleton
  }, [token]);

  const errorMessages = {
    '': '',
    missingToken: t('pages.magicLogin.errors.missingToken', 'Link di accesso mancante o non valido.'),
    invalid: t('pages.magicLogin.errors.invalid', 'Questo link non è più valido.'),
    exchange: t('pages.magicLogin.errors.exchange', 'Accesso non riuscito, riprova.'),
  } as const;
  const errorMessage = errorMessages[error];

  const monthLabel = useMemo(
    () => (preview ? capitalize(format(new Date(preview.periodYear, preview.periodMonth - 1, 1), 'LLLL yyyy', { locale: dateLocale })) : ''),
    [preview, dateLocale],
  );
  const monthKey = preview ? `${preview.periodYear}-${String(preview.periodMonth).padStart(2, '0')}` : '';
  const expiresLabel = preview ? format(new Date(preview.expiresAt), 'd MMMM yyyy', { locale: dateLocale }) : '';

  const handleEnter = async () => {
    if (!token) return;
    setEntering(true);
    setError('');
    try {
      const session = await exchangeMagicLink(token);
      applyAuthPayload(session);
      navigate(`/timesheet?month=${monthKey}`, { replace: true });
    } catch {
      setError('exchange');
      setEntering(false);
    }
  };

  if (loading) {
    return (
      <div className={pageClass}>
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="flex items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
            {t('pages.magicLogin.loading', 'Verifica del link in corso…')}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className={pageClass}>
        <Card className="w-full max-w-md border-red-200 shadow-lg dark:border-red-900">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
              <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-2xl font-bold">{t('pages.magicLogin.errors.title', 'Link non valido')}</CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          <CardFooter className="flex flex-col gap-4">
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">{t('pages.magicLogin.backToLogin', 'Vai al login con password')}</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className={pageClass}>
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900">
            <CalendarCheck className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <CardTitle className="text-2xl font-bold">{t('pages.magicLogin.greeting', 'Ciao {{name}}', { name: preview.name })}</CardTitle>
          <CardDescription>
            {preview.tenantName
              ? t('pages.magicLogin.description', 'Stai per entrare in {{tenant}} per il timesheet di {{month}}.', {
                  tenant: preview.tenantName,
                  month: monthLabel,
                })
              : t('pages.magicLogin.descriptionNoTenant', 'Stai per entrare nel timesheet di {{month}}.', { month: monthLabel })}
          </CardDescription>
          <p className="text-xs text-muted-foreground">{preview.email}</p>
        </CardHeader>

        <CardContent className="space-y-4">
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
          <Button type="button" className="w-full bg-orange-500 hover:bg-orange-600" onClick={handleEnter} disabled={entering}>
            {entering ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {entering ? t('common.loading', 'Caricamento…') : t('pages.magicLogin.enter', 'Entra nel timesheet')}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            {t('pages.magicLogin.validUntil', 'Questo link è personale e vale fino al {{date}}. Non inoltrarlo.', { date: expiresLabel })}
          </p>
        </CardContent>

        <CardFooter className="justify-center">
          <Link to="/login" className="text-sm text-orange-600 hover:underline">
            {t('pages.magicLogin.usePassword', 'Preferisci usare la password?')}
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
};

export default MagicLogin;
